import React, { useMemo } from 'react';
import ReactDatePicker, { DatePickerProps } from 'react-datepicker';
import { isFuture, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import RangeHint from './RangeHint';

import 'react-datepicker/dist/react-datepicker.css';
import './index.scss';

type DesignSystemDatePickerProps = DatePickerProps & {
  containerClass?: string;
};

export default function DatePicker(
  {
    dateFormat = 'P',
    dropdownMode = 'select',
    locale = ptBR,
    showMonthDropdown = true,
    showYearDropdown = true,
    showMonthYearPicker = false,
    autoComplete = 'off',
    inline = false,
    containerClass = '',
    includeDates,
    includeDateIntervals,
    openToDate,
    selected,
    startDate,
    endDate,
    ...otherProps
  }: DesignSystemDatePickerProps,
) {
  // selectsRange discrimina a union de tipos da lib (ela muda a assinatura de
  // onChange), entao desestruturar quebraria o narrowing no repasse. Lemos o
  // valor sem tirar do spread.
  const selectsRange = Boolean((otherProps as { selectsRange?: boolean }).selectsRange);
  const hideHeader = (showMonthDropdown || showYearDropdown) && !showMonthYearPicker;
  const hideHeaderClass = hideHeader ? 'datepicker-hide-header' : '';
  const inlineClass = inline ? 'ds-inline' : '';
  const effectiveOpenToDate = useMemo(() => {
    if (selected) return selected;
    if (startDate && endDate) return endDate;
    // Intervalo pela metade: o primeiro clique ja foi dado e o segundo ainda
    // nao. Sem ancorar no inicio, openToDate cairia no fallback de hoje e a lib
    // renavegaria o mes exibido (ela compara com isSameDay), tirando o
    // calendario de onde o usuario acabou de clicar.
    if (selectsRange && startDate) return startDate;
    if (openToDate) return openToDate;
    if (includeDates?.length || includeDateIntervals?.length) {
      const optionsStart = includeDates?.[0] || includeDateIntervals?.[0]?.start as Date;
      const optionsEnd = includeDates?.at(-1) || includeDateIntervals?.at(-1)?.end as Date;
      const optionsStartInThePast = isPast(optionsStart);
      const optionsEndInTheFuture = isFuture(optionsEnd);
      if (optionsStartInThePast && optionsEndInTheFuture) return new Date();
      if (!optionsStartInThePast) return optionsStart;
      return optionsEnd;
    }
    return new Date();
  }, [openToDate, includeDates, includeDateIntervals, selected, selectsRange, startDate, endDate]);

  return (
    <div className={`text-center ${containerClass} ${hideHeaderClass} ${inlineClass}`}>
      <ReactDatePicker
        selected={selected}
        startDate={startDate}
        endDate={endDate}
        includeDates={includeDates}
        includeDateIntervals={includeDateIntervals}
        openToDate={effectiveOpenToDate}
        dateFormat={dateFormat}
        dropdownMode={dropdownMode}
        locale={locale}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        showMonthYearPicker={showMonthYearPicker}
        autoComplete={autoComplete}
        inline={inline}
        calendarClassName={selectsRange ? 'ds-datepicker ds-datepicker-range' : 'ds-datepicker'}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...otherProps}
      >
        {selectsRange ? <RangeHint startDate={startDate} endDate={endDate} /> : undefined}
      </ReactDatePicker>
    </div>
  );
}
